import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import SessionExpiredModal from './components/common/SessionExpiredModal';

// Layout
import DashboardLayout from './components/layout/DashboardLayout';
import TourProvider from './components/tour/TourProvider';

// Auth Pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import MatchmakerRegister from './pages/auth/MatchmakerRegister';

// Dashboard Pages
import Dashboard from './pages/Dashboard';
const Profiles = lazy(() => import('./pages/profiles/Profiles'));
const ProfileDetail = lazy(() => import('./pages/profiles/ProfileDetail'));
const ProfileForm = lazy(() => import('./pages/profiles/ProfileForm'));
const CoachProfileEdit = lazy(() => import('./pages/profiles/CoachProfileEdit'));
const PublicProfile = lazy(() => import('./pages/profiles/PublicProfile'));
const ProfileLayoutEditor = lazy(() => import('./pages/profiles/ProfileLayoutEditor'));
const AssignmentRequests = lazy(() => import('./pages/profiles/AssignmentRequests'));
const Directory = lazy(() => import('./pages/directory/Directory'));
const DirectoryProfileDetail = lazy(() => import('./pages/directory/DirectoryProfileDetail'));
const Matches = lazy(() => import('./pages/matches/Matches'));
const MatchDetail = lazy(() => import('./pages/matches/MatchDetail'));
const Messages = lazy(() => import('./pages/messages/Messages'));
const Notifications = lazy(() => import('./pages/notifications/Notifications'));
const Chat = lazy(() => import('./pages/chat/Chat'));
const ChatSettings = lazy(() => import('./pages/chat/ChatSettings'));
const Tasks = lazy(() => import('./pages/tasks/Tasks'));
const Calendar = lazy(() => import('./pages/calendar/Calendar'));
const Affiliate = lazy(() => import('./pages/affiliate/Affiliate'));
// 活动与成长项目 (phase 1)
const OrganizerApply = lazy(() => import('./pages/organizer/OrganizerApply'));
const OrganizerEvents = lazy(() => import('./pages/organizer/OrganizerEvents'));
const OrganizerEventEditor = lazy(() => import('./pages/organizer/OrganizerEventEditor'));
const OrganizerAttendees = lazy(() => import('./pages/organizer/OrganizerAttendees'));
const OrganizerCheckin = lazy(() => import('./pages/organizer/OrganizerCheckin'));
const OrganizerEarnings = lazy(() => import('./pages/organizer/OrganizerEarnings'));
const AdminExperiences = lazy(() => import('./pages/admin/AdminExperiences'));
const ClientDiscover = lazy(() => import('./pages/client/ClientDiscover'));
const ClientExperienceDetail = lazy(() => import('./pages/client/ClientExperienceDetail'));
const PublicExperience = lazy(() => import('./pages/public/PublicExperience'));
const ClientTickets = lazy(() => import('./pages/client/ClientTickets'));
const ClientOrders = lazy(() => import('./pages/client/ClientOrders'));
const ClientServiceReports = lazy(() => import('./pages/client/ClientServiceReports'));
// 恋爱课堂 (love-skills course)
const ClientLearn = lazy(() => import('./pages/client/ClientLearn'));
const ClientLesson = lazy(() => import('./pages/client/ClientLesson'));
const AdminCourses = lazy(() => import('./pages/admin/AdminCourses'));
const AdminCourseEditor = lazy(() => import('./pages/admin/AdminCourseEditor'));
const AdminCourseImport = lazy(() => import('./pages/admin/AdminCourseImport'));
const Terms = lazy(() => import('./pages/public/Terms'));
const ServiceReports = lazy(() => import('./pages/service/ServiceReports'));
const ServiceSupervision = lazy(() => import('./pages/service/ServiceSupervision'));
const Guide = lazy(() => import('./pages/guide/Guide'));
const Impersonate = lazy(() => import('./pages/auth/Impersonate'));
const Billing = lazy(() => import('./pages/billing/Billing'));
const Settings = lazy(() => import('./pages/settings/Settings'));
const DatingSchedules = lazy(() => import('./pages/dating/DatingSchedules'));
const Meetings = lazy(() => import('./pages/Meetings'));
const Help = lazy(() => import('./pages/help/Help'));
const KpiV2 = lazy(() => import('./pages/kpi-v2/KpiV2'));
const Connections = lazy(() => import('./pages/connections/Connections'));

// Admin / Coach / Heavy feature pages are lazy-loaded so the initial bundle
// stays small (helps mobile + cross-border users a lot). Each becomes its
// own webpack chunk that downloads on first visit only.
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminMatchmakerProfile = lazy(() => import('./pages/admin/AdminMatchmakerProfile'));
const AdminRoles = lazy(() => import('./pages/admin/AdminRoles'));
const AdminProfiles = lazy(() => import('./pages/admin/AdminProfiles'));
const AdminAffiliates = lazy(() => import('./pages/admin/AdminAffiliates'));
const AdminPlans = lazy(() => import('./pages/admin/AdminPlans'));
const Training = lazy(() => import('./pages/training/Training'));
const AdminTraining = lazy(() => import('./pages/admin/AdminTraining'));
const AdminLogs = lazy(() => import('./pages/admin/AdminLogs'));
const AdminApiTokens = lazy(() => import('./pages/admin/AdminApiTokens'));
const AdminProfileEdits = lazy(() => import('./pages/admin/AdminProfileEdits'));
const AdminDatingSchedules = lazy(() => import('./pages/admin/AdminDatingSchedules'));
const AdminBilling = lazy(() => import('./pages/admin/AdminBilling'));
const AdminCustomFields = lazy(() => import('./pages/admin/AdminCustomFields'));
const AdminCoaches = lazy(() => import('./pages/admin/AdminCoaches'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminAutomationSettings = lazy(() => import('./pages/admin/AdminAutomationSettings'));
const AdminAutomation = lazy(() => import('./pages/admin/AdminAutomation'));
const AdminStarsign = lazy(() => import('./pages/admin/AdminStarsign'));
const AdminStarsignReports = lazy(() => import('./pages/admin/AdminStarsignReports'));
const AdminTrends = lazy(() => import('./pages/admin/AdminTrends'));
const AdminMatches = lazy(() => import('./pages/admin/AdminMatches'));
const AdminTranslations = lazy(() => import('./pages/admin/AdminTranslations'));
const AdminDatabaseSync = lazy(() => import('./pages/admin/AdminDatabaseSync'));
const AdminContracts = lazy(() => import('./pages/admin/AdminContracts'));
const AdminKPI = lazy(() => import('./pages/admin/AdminKPI'));
const AdminFinance = lazy(() => import('./pages/admin/AdminFinance'));
const AdminUpgradeRequests = lazy(() => import('./pages/admin/AdminUpgradeRequests'));
const CoachKPI = lazy(() => import('./pages/coach/CoachKPI'));
const LeaderCoachKPI = lazy(() => import('./pages/coach/LeaderCoachKPI'));
const LeaderCoachKPIPerformance = lazy(() => import('./pages/coach/LeaderCoachKPIPerformance'));
const SupervisorKPI = lazy(() => import('./pages/supervisor/SupervisorKPI'));
const Payroll = lazy(() => import('./pages/payroll/Payroll'));
const MyPayroll = lazy(() => import('./pages/payroll/MyPayroll'));
// Public + meeting routes are lazy-loaded so first-time visitors (especially
// from networks with high latency like China) don't have to download the
// whole admin/coach/client bundle just to open a meeting or questionnaire link.
const PublicQuestionnaire = lazy(() => import('./pages/public/PublicQuestionnaire'));
const EventRegister = lazy(() => import('./pages/public/EventRegister'));
const PublicMatchReview = lazy(() => import('./pages/public/PublicMatchReview'));
const PublicConfirmation = lazy(() => import('./pages/public/PublicConfirmation'));
const PartnerPortraitReview = lazy(() => import('./pages/public/PartnerPortraitReview'));
const SignContract = lazy(() => import('./pages/public/SignContract'));
const VideoMeeting = lazy(() => import('./pages/meeting/VideoMeeting'));
const AudioCall = lazy(() => import('./pages/meeting/AudioCall'));
const AudioCalls = lazy(() => import('./pages/AudioCalls'));
const PublicBooking = lazy(() => import('./pages/public/PublicBooking'));
const InterviewBooking = lazy(() => import('./pages/public/InterviewBooking'));
const InterviewManage = lazy(() => import('./pages/public/InterviewManage'));
const PhoneUpload = lazy(() => import('./pages/public/PhoneUpload'));
const InterviewBookings = lazy(() => import('./pages/interview/InterviewBookings'));
const InterviewAvailability = lazy(() => import('./pages/interview/InterviewAvailability'));
const StarsignReport = lazy(() => import('./pages/public/StarsignReport'));
const SharedCollection = lazy(() => import('./pages/directory/SharedCollection'));

// Client Portal Pages — also lazy. Matchmaker users never load these, and
// client users only load them after the (synchronous) ClientLogin succeeds.
import ClientLayout from './components/layout/ClientLayout';
import { ClientRouteSkeleton } from './components/client/LuxeSkeletons';
import ClientLogin from './pages/client/ClientLogin';
const ClientForgotPassword = lazy(() => import('./pages/client/ClientForgotPassword'));
const ClientHome = lazy(() => import('./pages/client/ClientHome'));
const ClientDashboard = lazy(() => import('./pages/client/ClientDashboard'));
const ClientEditProfile = lazy(() => import('./pages/client/ClientEditProfile'));
const ClientMessages = lazy(() => import('./pages/client/ClientMessages'));
const ClientTrendDetail = lazy(() => import('./pages/client/ClientTrendDetail'));
const ClientTrendCompose = lazy(() => import('./pages/client/ClientTrendCompose'));
const ClientMyTrends = lazy(() => import('./pages/client/ClientMyTrends'));
const ClientChat = lazy(() => import('./pages/client/ClientChat'));
const ClientAI = lazy(() => import('./pages/client/ClientAI'));

// Profile-only users (members on the client portal) can authenticate but
// must never land on the admin/backend tree at `/`. Rather than redirecting
// in a way that risks loops, render an inline access-denied screen with a
// "Back" button that sends them to /client.
const ProfileOnlyBlocked = () => {
  const lang = (typeof navigator !== 'undefined' && /^zh/i.test(navigator.language || '')) ? 'zh' : 'en';
  const isZh = lang === 'zh';
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: '#FAF7F2' }}>
      <div style={{ maxWidth: 420, background: '#fff', border: '1px solid #E8E1D5', borderRadius: 14, padding: '36px 28px', textAlign: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(220,53,69,0.08)', color: '#dc3545', fontSize: 28, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}>!</div>
        <h2 style={{ margin: '0 0 8px', fontSize: 20, color: '#2C2C2C' }}>{isZh ? '该页面无法访问' : 'Page Not Accessible'}</h2>
        <p style={{ margin: '0 0 22px', fontSize: 14, color: '#6B6B6B', lineHeight: 1.55 }}>
          {isZh ? '您的账号没有访问后台的权限。请返回客户端继续使用。' : "Your account doesn't have permission to access the admin area. Please go back to the client portal."}
        </p>
        <button
          onClick={() => { window.location.href = `${process.env.PUBLIC_URL}/client`; }}
          style={{ padding: '10px 22px', border: 'none', background: 'linear-gradient(135deg,var(--color-burgundy) 0%,#5C2E33 100%)', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
        >
          {isZh ? '返回' : 'Back'}
        </button>
      </div>
    </div>
  );
};

// Internal matchmakers register with status `pending` and must be approved by an
// admin (Affiliates → 联盟成员审核) before they can use the backend. Until then
// this full-screen notice replaces the entire backend tree, and the API rejects
// every route except /auth/me with 403 PENDING_APPROVAL — so there is nothing
// behind it to reach.
const PendingApprovalBlocked = () => {
  const { logout } = useAuth();
  const lang = (typeof navigator !== 'undefined' && /^zh/i.test(navigator.language || '')) ? 'zh' : 'en';
  const isZh = lang === 'zh';
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: '#FAF7F2' }}>
      <div style={{ maxWidth: 460, background: '#fff', border: '1px solid #E8E1D5', borderRadius: 14, padding: '36px 28px', textAlign: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(217,119,6,0.10)', color: '#D97706', fontSize: 28, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}>⏳</div>
        <h2 style={{ margin: '0 0 8px', fontSize: 20, color: '#2C2C2C' }}>
          {isZh ? '账号待审核' : 'Account Pending Approval'}
        </h2>
        <p style={{ margin: '0 0 22px', fontSize: 14, color: '#6B6B6B', lineHeight: 1.55 }}>
          {isZh
            ? '您的注册已提交，需要管理员审核通过后才能访问后台。审核通过后重新登录即可，感谢您的耐心等待。'
            : 'Your registration has been submitted and is awaiting administrator approval. Once approved, sign in again to access the dashboard. Thank you for your patience.'}
        </p>
        <button
          onClick={() => { try { logout(); } catch (e) { /* fall through */ } window.location.href = `${process.env.PUBLIC_URL}/admin/login`; }}
          style={{ padding: '10px 22px', border: 'none', background: 'linear-gradient(135deg,var(--color-burgundy) 0%,#5C2E33 100%)', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
        >
          {isZh ? '退出登录' : 'Sign Out'}
        </button>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, isAuthenticated, loading, hasRole } = useAuth();

  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  // Awaiting admin approval → the whole backend is replaced by the notice.
  if (user?.status === 'pending') {
    return <PendingApprovalBlocked />;
  }

  // Block profile-only users from the backend tree. Compute the union of
  // primary role + secondary roles array — if `profile` is the only role,
  // they can't be here.
  const _primaryRole = (user?.role || '').toLowerCase();
  const _secondaryRoles = Array.isArray(user?.roles) ? user.roles.map(r => (r || '').toLowerCase()) : [];
  const _allRoles = new Set([_primaryRole, ..._secondaryRoles].filter(Boolean));
  const isProfileOnly = _allRoles.size > 0 && _allRoles.size === 1 && _allRoles.has('profile');
  if (isProfileOnly) {
    return <ProfileOnlyBlocked />;
  }

  if (requiredRole && !hasRole(requiredRole)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// /profiles?favorites=1 → render the Directory 我的收藏 (favorites) view (same
// component + ?favorites=1 deep-link as /directory). Otherwise the profile list.
const ProfilesOrFavorites = () => {
  const location = useLocation();
  const favorites = new URLSearchParams(location.search).get('favorites') === '1';
  return favorites ? <Directory /> : <Profiles />;
};

// Profile Edit Router — coach + leader_coach get admin-style modal; others get step wizard
const ProfileEditRouter = () => {
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  const useModalForm = role === 'coach' || role === 'leader_coach';
  return useModalForm ? <CoachProfileEdit /> : <ProfileForm />;
};

// Public Route Component (redirects to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// While a lazy page chunk loads: member-portal URLs get a skeleton of the page
// that is coming (with the portal chrome); everything else keeps the spinner.
const RouteFallback = () => {
  const path = (typeof window !== 'undefined' ? window.location.pathname : '').replace(/^\/preview(?=\/)/, '');
  if (/^\/client(\/|$)/.test(path) && !/^\/client\/login/.test(path)) return <ClientRouteSkeleton pathname={path} />;
  return (
    <div className="loading-overlay">
      <div className="loading-spinner"></div>
    </div>
  );
};

function App() {
  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Client Login (default login page) */}
      <Route path="/login" element={<ClientLogin />} />
      {/* Legacy /client/login → /login (some bookmarks / outbound links still point here) */}
      <Route path="/client/login" element={<Navigate to="/login" replace />} />
      <Route path="/forgot" element={<ClientForgotPassword />} />
      <Route path="/reset-password" element={<ClientForgotPassword />} />

      {/* Admin/Coach/Matchmaker Auth Routes */}
      <Route
        path="/admin/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/register/matchmaker"
        element={
          <PublicRoute>
            <MatchmakerRegister />
          </PublicRoute>
        }
      />

      {/* Public Profile Route - accessible without login */}
      <Route path="/public/profile/:slug" element={<PublicProfile />} />

      {/* Legacy /profile/:slug links (e.g. client registration login URLs) used
          to drop clients into the backend dashboard shell. Redirect them to the
          client login page instead. Public (outside ProtectedRoute) so it works
          for logged-out clients too. */}
      <Route path="/profile/:slug" element={<Navigate to="/client/login" replace />} />

      {/* Public Questionnaire Route - accessible without login */}
      <Route path="/questionnaire/:token" element={<PublicQuestionnaire />} />
      <Route path="/event-register" element={<EventRegister />} />
      {/* 活动分享页 — public, no login */}
      <Route path="/e/:slug" element={<PublicExperience />} />
      <Route path="/terms" element={<Terms />} />

      {/* Admin-generated affiliate login link — stores the token and drops the
          visitor straight into the partner's dashboard (works in any browser). */}
      <Route path="/impersonate" element={<Impersonate />} />

      {/* Public Match Review Route - accessible without login */}
      <Route path="/match-review/:token" element={<PublicMatchReview />} />
      <Route path="/partner-portrait-review/:token" element={<PartnerPortraitReview />} />
      <Route path="/sign/:token" element={<SignContract />} />

      {/* Public Confirmation Route - accessible without login */}
      <Route path="/confirmation/:token" element={<PublicConfirmation />} />

      {/* Public Meeting Room - accessible without login */}
      <Route path="/meeting/:channelName" element={<VideoMeeting />} />

      {/* Public one-on-one Audio Call room - accessible without login */}
      <Route path="/audio-call/:channelName" element={<AudioCall />} />

      {/* Public sharable booking page — no auth, slug-gated */}
      <Route path="/book/:slug" element={<PublicBooking />} />

      {/* Public interview registration + booking — no auth. The manage link
          (token from the confirmation email) views / changes / cancels it. */}
      <Route path="/interview" element={<InterviewBooking />} />
      <Route path="/interview/manage/:token" element={<InterviewManage />} />

      {/* 扫码上传 — the phone page opened from the QR code in 客户记录. Public, no login; the
          one-time token is the only key and only allows uploading into one slot. */}
      <Route path="/u/:token" element={<PhoneUpload />} />

      {/* Public star-sign (命盘) report request — no auth */}
      <Route path="/starsign-report" element={<StarsignReport />} />

      {/* Shared Collection - accessible without login */}
      <Route path="/shared/collection/:token" element={<SharedCollection />} />

      {/* Protected Dashboard Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            {/* 操作引导 (guided tours, 示例模式) — wraps the staff shell so the sidebar,
                the pages and the 客户工作台 can all reach it. */}
            <TourProvider>
              <DashboardLayout />
            </TourProvider>
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        
        {/* Profiles — /profiles?favorites=1 shows the same 我的收藏 (favorites)
            view as /directory?favorites=1; otherwise the normal profile list. */}
        <Route path="profiles" element={<ProfilesOrFavorites />} />
        <Route path="profiles/new" element={<ProfileForm />} />
        <Route path="profiles/layout-editor" element={<ProfileLayoutEditor />} />
        <Route path="profiles/:id" element={<ProfileDetail />} />
        <Route path="profiles/:id/edit" element={<ProfileEditRouter />} />
        <Route path="assignment-requests" element={<AssignmentRequests />} />
        {/* 月度服务报告 (VIP 服务流程 5.0) — the API scopes coaches to their own members. */}
        <Route path="service-reports" element={<ServiceReports />} />
        <Route path="service-supervision" element={<ServiceSupervision />} />
        <Route path="guide" element={<Guide />} />
        
        {/* Directory */}
        <Route path="directory" element={<Directory />} />
        <Route path="directory/:id" element={<DirectoryProfileDetail />} />
        
        {/* Matches */}
        <Route path="matches" element={<Matches />} />
        <Route path="matches/:id" element={<MatchDetail />} />
        
        {/* Communication */}
        <Route path="messages" element={<Messages />} />
        <Route path="chat" element={<Chat />} />
        <Route path="chat/settings" element={<ChatSettings />} />
        <Route path="notifications" element={<Notifications />} />

        {/* Tasks & Calendar */}
        <Route path="tasks" element={<Tasks />} />
        <Route path="calendar" element={<Calendar />} />
        
        {/* Dating Schedules */}
        <Route path="dating-schedules" element={<DatingSchedules />} />

        {/* Video Meetings (general-purpose page — coach/leader_coach/matchmaker/admin) */}
        <Route path="meetings" element={<Meetings />} />

        {/* One-on-one Audio Calls (coach / leader_coach console + admin record view) */}
        <Route path="audio-calls" element={<AudioCalls />} />

        {/* Help — role-specific tutorials. Only visible to coach / leader /
            supervisor variants (admins have their own docs). */}
        <Route
          path="help"
          element={
            <ProtectedRoute requiredRole={['coach', 'Coach', 'leader_coach', 'supervisor', 'supervisor_coach', 'Supervisor_coach', 'admin', 'super_admin']}>
              <Help />
            </ProtectedRoute>
          }
        />

        {/* KPIv2 — after-sales service tickets / performance. */}
        <Route
          path="kpi-v2"
          element={
            <ProtectedRoute requiredRole={['coach', 'Coach', 'leader_coach', 'supervisor', 'supervisor_coach', 'Supervisor_coach', 'admin', 'super_admin', 'manager', 'Manager']}>
              <KpiV2 />
            </ProtectedRoute>
          }
        />

        {/* 面谈预约 / 可面谈时间 — interviewer-role users + admins. */}
        <Route
          path="interview-bookings"
          element={
            <ProtectedRoute requiredRole={['interviewer', 'admin', 'super_admin']}>
              <InterviewBookings />
            </ProtectedRoute>
          }
        />
        <Route
          path="interview-availability"
          element={
            <ProtectedRoute requiredRole={['interviewer', 'admin', 'super_admin']}>
              <InterviewAvailability />
            </ProtectedRoute>
          }
        />

        {/* Connections - for external matchmakers and admin */}
        <Route path="connections" element={<Connections />} />
        
        {/* Affiliate & Billing */}
        <Route path="affiliate" element={<Affiliate />} />
        {/* 活动与成长项目 — organiser side */}
        <Route path="organizer/apply" element={<OrganizerApply />} />
        <Route path="organizer/events" element={<OrganizerEvents key="event" />} />
        <Route path="organizer/events/new" element={<OrganizerEventEditor key="event-editor" />} />
        <Route path="organizer/events/:id/edit" element={<OrganizerEventEditor key="event-editor" />} />
        <Route path="organizer/events/:id/attendees" element={<OrganizerAttendees key="event" />} />
        <Route path="organizer/programs" element={<OrganizerEvents key="program" />} />
        <Route path="organizer/programs/new" element={<OrganizerEventEditor key="program-editor" />} />
        <Route path="organizer/programs/:id/edit" element={<OrganizerEventEditor key="program-editor" />} />
        <Route path="organizer/programs/:id/attendees" element={<OrganizerAttendees key="program" />} />
        <Route path="organizer/checkin" element={<OrganizerCheckin />} />
        <Route path="organizer/checkin/:id" element={<OrganizerCheckin />} />
        <Route path="organizer/earnings" element={<OrganizerEarnings />} />
        <Route path="training" element={<Training />} />
        <Route path="billing" element={<Billing />} />
        
        {/* Settings */}
        <Route path="settings" element={<Settings />} />

        {/* Coach KPI Performance */}
        <Route path="coach/kpi" element={<CoachKPI />} />

        {/* Leader Coach KPI Performance */}
        <Route
          path="leader-coach/kpi-performance"
          element={
            <ProtectedRoute requiredRole={['leader_coach', 'supervisor_coach', 'Supervisor_coach', 'super_admin', 'admin']}>
              <LeaderCoachKPIPerformance />
            </ProtectedRoute>
          }
        />

        {/* Leader Coach KPI Review */}
        <Route
          path="leader-coach/kpi"
          element={
            <ProtectedRoute requiredRole={['leader_coach', 'supervisor_coach', 'Supervisor_coach', 'super_admin', 'admin']}>
              <LeaderCoachKPI />
            </ProtectedRoute>
          }
        />

        {/* Supervisor KPI Review */}
        <Route path="supervisor/kpi" element={<SupervisorKPI />} />

        {/* Payroll — leader_coach drafts → supervisor approves → admin
            final-approves. Coach uses /my-payroll for the read-only slip. */}
        <Route
          path="payroll"
          element={
            <ProtectedRoute requiredRole={['leader_coach', 'supervisor_coach', 'admin', 'super_admin']}>
              <Payroll />
            </ProtectedRoute>
          }
        />
        <Route
          path="my-payroll"
          element={
            <ProtectedRoute requiredRole={['Coach', 'coach', 'leader_coach', 'supervisor_coach']}>
              <MyPayroll />
            </ProtectedRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="admin"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/users"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'admin']}>
              <AdminUsers />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/matchmaker/:matchmakerId"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'admin']}>
              <AdminMatchmakerProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/roles"
          element={
            <ProtectedRoute requiredRole={['super_admin']}>
              <AdminRoles />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/profiles"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'admin', 'editor']}>
              <AdminProfiles />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/affiliates"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'admin']}>
              <AdminAffiliates />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/courses"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'admin', 'coach', 'leader_coach', 'supervisor_coach', 'supervisor', 'manager']}>
              <AdminCourses />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/courses/import"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'admin']}>
              <AdminCourseImport />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/courses/lessons/:id"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'admin', 'coach', 'leader_coach', 'supervisor_coach', 'supervisor', 'manager']}>
              <AdminCourseEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/experiences"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'admin']}>
              <AdminExperiences />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/plans"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'admin']}>
              <AdminPlans />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/training"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'admin']}>
              <AdminTraining />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/logs"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'admin']}>
              <AdminLogs />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/api-tokens"
          element={
            <ProtectedRoute requiredRole={['super_admin']}>
              <AdminApiTokens />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/profile-edits"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'admin']}>
              <AdminProfileEdits />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/dating-schedules"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'admin']}>
              <AdminDatingSchedules />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/matches"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'admin']}>
              <AdminMatches />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/billing"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'admin']}>
              <AdminBilling />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/finance"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'admin']}>
              <AdminFinance />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/upgrade-requests"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'admin']}>
              <AdminUpgradeRequests />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/custom-fields"
          element={
            <ProtectedRoute requiredRole={['super_admin']}>
              <AdminCustomFields />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/coaches"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'admin']}>
              <AdminCoaches />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/settings"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'admin']}>
              <AdminSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/automation-settings"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'admin']}>
              <AdminAutomation />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/starsign-settings"
          element={
            <ProtectedRoute requiredRole="super_admin">
              <AdminStarsign />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/starsign-reports"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'admin']}>
              <AdminStarsignReports />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/trends"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'admin', 'editor']}>
              <AdminTrends />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/translations"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'admin']}>
              <AdminTranslations />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/database-sync"
          element={
            <ProtectedRoute requiredRole={['super_admin']}>
              <AdminDatabaseSync />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/contracts"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'admin']}>
              <AdminContracts />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/kpi"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'admin']}>
              <AdminKPI />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Client Portal Routes */}
      <Route path="/client" element={<ClientLayout />}>
        <Route index element={<ClientHome />} />
        <Route path="trends" element={<Navigate to="/client/discover" replace />} />
        <Route path="discover" element={<ClientDiscover />} />
        <Route path="events/:id" element={<ClientExperienceDetail />} />
        <Route path="programs/:id" element={<ClientExperienceDetail />} />
        <Route path="trends/new" element={<ClientTrendCompose />} />
        <Route path="trends/:trendId/edit" element={<ClientTrendCompose />} />
        <Route path="trends/:trendId" element={<ClientTrendDetail />} />
        <Route path="messages" element={<ClientMessages />} />
        <Route path="messages/:conversationId" element={<ClientChat />} />
        <Route path="me/tickets" element={<ClientTickets />} />
        <Route path="me/orders" element={<ClientOrders />} />
        <Route path="me/reports" element={<ClientServiceReports />} />
        <Route path="learn" element={<ClientLearn />} />
        <Route path="learn/:lessonId" element={<ClientLesson />} />
        <Route path="me/reports/:id" element={<ClientServiceReports />} />
        <Route path="me" element={<ClientDashboard />} />
        <Route path="me/edit" element={<ClientEditProfile />} />
        <Route path="me/matches" element={<ClientDashboard />} />
        <Route path="me/visitors" element={<ClientDashboard />} />
        <Route path="me/following" element={<ClientDashboard />} />
        <Route path="me/album" element={<ClientDashboard />} />
        <Route path="me/trends" element={<ClientDashboard />} />
        <Route path="me/my-trends" element={<ClientMyTrends />} />
        <Route path="me/password" element={<ClientDashboard />} />
        <Route path="me/schedules" element={<ClientDashboard />} />
        <Route path="me/settings" element={<ClientDashboard />} />
        {/* /profile/:id now redirects to the client login (handled by the
            public route above); the in-dashboard ClientProfile route is removed
            so clients are no longer dropped into the backend shell. */}
        <Route path="ai" element={<ClientAI />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    {/* Global re-login modal — shown in place when the backend session expires
        so the user keeps their place instead of being bounced to /login. */}
    <SessionExpiredModal />
    </Suspense>
  );
}

export default App;
